import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import TemplateService, { type CreateStepPayload, type UpdateStepPayload, type PreviewTemplateResponse } from '../../../../AppSettings/services/TemplateService';
import CustomFieldsService from '../../../../AppSettings/services/CustomfieldsService';
import ClientService from '../../../../AppSettings/services/ClientService';
import MatterService from '../../../../AppSettings/services/MatterService';
import EmployeeService from '../../../../AppSettings/services/EmployeeService';
import ToastService from '../../../../AppSettings/services/ToastService';
import useActiveEmployeeId from '../../../../AppSettings/hooks/useActiveEmployeeId';
import type { WorkflowTemplatePayload, WorkflowTemplateStep, WorkflowTemplateApplyPayload, WorkflowTemplateStepPayload } from '../../../../types/workflows';
import OffsetRuleEditor, { type OffsetRule } from './OffsetRuleEditor';
import { computeLocalTemplatePreview } from './localTemplatePreview';
import { format, parseISO, differenceInCalendarDays, eachMonthOfInterval } from 'date-fns';
import jsPDF from 'jspdf';
import './TemplateEditorPage.css';

/**
 * Workflow Template Builder - Template Editor Page
 * 
 * PRD3: Workflow Template Builder
 * This page provides the user interface for editing workflow templates.
 * 
 * Features:
 * - Tabbed interface: Metadata, Steps, Preview
 * - Metadata form with autosave (every 10 seconds)
 * - Form validation
 * - Save button for explicit saves
 */

type TabType = 'create' | 'preview';

interface SelectOption {
  value: string;
  label: string;
}

interface MetadataFormData {
  name: string;
  description: string;
  category: string;
  clientId: string; // appliesTo
  versionNote: string; // stored in metadata
  tags: string; // stored in metadata as array
}

const BUILT_IN_DATES = [
  { key: 'projectStart', label: 'Project Start Date' },
  { key: 'ifcDate', label: 'Issued for Construction (IFC) Date' },
  { key: 'constructionDate', label: 'Construction Start Date' },
];

const TemplateEditorPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const isCreateMode = !templateId || templateId === 'create';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const employeeId = useActiveEmployeeId();
  
  const [activeTab, setActiveTab] = useState<TabType>('create');
  const [formData, setFormData] = useState<MetadataFormData>({
    name: '',
    description: '',
    category: '',
    clientId: '',
    versionNote: '',
    tags: '',
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  
  // Step management state
  const [showStepModal, setShowStepModal] = useState(false);
  const [showStepAdditional, setShowStepAdditional] = useState(false);
  const [editingStep, setEditingStep] = useState<WorkflowTemplateStep | null>(null);
  const [stepFormData, setStepFormData] = useState<{
    name: string;
    category: string;
    discipline: string;
    role: string;
    description: string;
    offsetRule: OffsetRule;
  }>({
    name: '',
    category: '',
    discipline: '',
    role: '',
    description: '',
    offsetRule: {
      offsetType: 'after_project_start',
      offsetDays: 0,
    },
  });
  const [draggedStepIndex, setDraggedStepIndex] = useState<number | null>(null);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState<string | null>(null);
  const [editingLocalStepId, setEditingLocalStepId] = useState<string | null>(null);
  
  // Local steps state for create mode (before template is saved)
  const [localSteps, setLocalSteps] = useState<Array<{
    tempId: string; // Temporary ID for local state
    order: number;
    name: string;
    category?: string;
    discipline?: string;
    role?: string;
    description?: string;
    offsetRule: OffsetRule;
  }>>([]);
  
  // Preview state
  const [anchorDates, setAnchorDates] = useState<Record<string, string>>({});
  const [enabledBuiltInDates, setEnabledBuiltInDates] = useState<string[]>([]);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showKeyDatesSection, setShowKeyDatesSection] = useState(false);
  const [customAnchors, setCustomAnchors] = useState<Array<{
    id: string;
    key: string;
    label: string;
    date?: string;
  }>>([]);
  const [anchorGroups, setAnchorGroups] = useState<Array<{ id: number; name: string; fieldIds: number[] }>>([]);
  const [anchorFields, setAnchorFields] = useState<Array<{ customfield_id: number; custom_field: string; type?: string }>>([]);
  const [selectedAnchorGroupId, setSelectedAnchorGroupId] = useState<string>('');
  const [newAnchorName, setNewAnchorName] = useState('');
  const [previewData, setPreviewData] = useState<PreviewTemplateResponse | null>(null);
  const [previewViewMode, setPreviewViewMode] = useState<'list' | 'chart'>('list');
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const DISCIPLINE_COLORS: Record<string, { bg: string; fg: string; bar: string }> = useMemo(() => ({
    'Architectural': { bg: '#eff6ff', fg: '#1e40af', bar: '#3b82f6' },
    'Structural': { bg: '#fef3c7', fg: '#92400e', bar: '#f59e0b' },
    'Mechanical': { bg: '#ecfdf5', fg: '#065f46', bar: '#10b981' },
    'Electrical': { bg: '#fef2f2', fg: '#991b1b', bar: '#ef4444' },
    'Civil': { bg: '#f5f3ff', fg: '#5b21b6', bar: '#8b5cf6' },
    'Plumbing': { bg: '#ecfeff', fg: '#155e75', bar: '#06b6d4' },
    'Landscape': { bg: '#f0fdf4', fg: '#166534', bar: '#22c55e' },
    'Interior': { bg: '#fff7ed', fg: '#9a3412', bar: '#f97316' },
    'Environmental': { bg: '#f0fdfa', fg: '#134e4a', bar: '#14b8a6' },
    'Fire Protection': { bg: '#fef2f2', fg: '#9f1239', bar: '#e11d48' },
    '_default': { bg: '#f1f5f9', fg: '#475569', bar: '#6366f1' },
  }), []);

  const getDisciplineColor = useCallback((discipline?: string | null) => {
    if (!discipline) return DISCIPLINE_COLORS['_default'];
    return DISCIPLINE_COLORS[discipline] || DISCIPLINE_COLORS['_default'];
  }, [DISCIPLINE_COLORS]);

  const ganttData = useMemo(() => {
    if (!previewData || !previewData.timeline?.length) return null;

    const parsed = [...previewData.timeline].map((item) => {
      const startDate = parseISO(item.plannedDate);
      const endDateRaw = item.endDate ? parseISO(item.endDate) : startDate;
      const endDate = endDateRaw < startDate ? startDate : endDateRaw;
      const durationDays = differenceInCalendarDays(endDate, startDate);

      return { ...item, startDate, endDate, durationDays };
    });

    const sorted = parsed.sort(
      (a, b) => a.startDate.getTime() - b.startDate.getTime()
    );

    const minDate = sorted.reduce(
      (acc, item) => (item.startDate < acc ? item.startDate : acc),
      sorted[0].startDate
    );
    const maxDate = sorted.reduce(
      (acc, item) => (item.endDate > acc ? item.endDate : acc),
      sorted[0].endDate
    );
    const totalDays = Math.max(1, differenceInCalendarDays(maxDate, minDate) + 1);

    const bars = sorted.map((item) => {
      const startOffset = differenceInCalendarDays(item.startDate, minDate);
      const endOffset = differenceInCalendarDays(item.endDate, minDate);
      const left = (startOffset / totalDays) * 100;
      const rawWidth = (Math.max(1, endOffset - startOffset + 1) / totalDays) * 100;
      const isMilestone = item.durationDays === 0;
      const width = isMilestone ? Math.max(rawWidth, 0.6) : rawWidth;
      const color = getDisciplineColor(item.discipline);

      return {
        id: item.stepId,
        name: item.stepName || (item as any).name || 'Untitled Step',
        start: item.startDate,
        end: item.endDate,
        offsetType: item.offsetType,
        offsetDays: item.offsetDays,
        discipline: item.discipline || null,
        category: item.category || null,
        stepOrder: item.stepOrder ?? 0,
        durationDays: item.durationDays,
        isMilestone,
        left,
        width,
        color,
      };
    });

    const disciplines = Array.from(new Set(sorted.map(s => s.discipline).filter(Boolean))) as string[];

    let monthMarkers: Array<{ label: string; left: number }> = [];
    try {
      const months = eachMonthOfInterval({ start: minDate, end: maxDate });
      monthMarkers = months.map(m => ({
        label: format(m, 'MMM yyyy'),
        left: (differenceInCalendarDays(m, minDate) / totalDays) * 100,
      }));
    } catch { /* interval too small */ }

    const todayOffset = differenceInCalendarDays(new Date(), minDate);
    const todayLeft = totalDays > 0 ? (todayOffset / totalDays) * 100 : -1;
    const showToday = todayLeft >= 0 && todayLeft <= 100;

    return { bars, minDate, maxDate, totalDays, disciplines, monthMarkers, todayLeft, showToday };
  }, [previewData, getDisciplineColor]);

  // Custom anchors helpers
  const slugifyAnchor = useCallback((label: string) => {
    return label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      || `anchor-${Date.now()}`;
  }, []);

  const handleAddCustomAnchor = useCallback(() => {
    const label = newAnchorName.trim();
    if (!label) {
      ToastService.warning('Please enter an anchor name');
      return;
    }
    const key = slugifyAnchor(label);
    const exists = customAnchors.some(a => a.key === key);
    if (exists) {
      ToastService.warning('Anchor with this name already exists');
      return;
    }
    const anchor = {
      id: `anchor-${Date.now()}`,
      key,
      label,
    };
    setCustomAnchors(prev => [...prev, anchor]);
    setHasUnsavedChanges(true);
    setNewAnchorName('');
  }, [customAnchors, newAnchorName, slugifyAnchor]);

  const handleUpdateCustomAnchorDate = useCallback((id: string, date: string) => {
    setCustomAnchors(prev => prev.map(anchor => anchor.id === id ? { ...anchor, date } : anchor));
  }, []);

  const handleRemoveCustomAnchor = useCallback((id: string) => {
    setCustomAnchors(prev => prev.filter(anchor => anchor.id !== id));
    setHasUnsavedChanges(true);
  }, []);

  const handleAddBuiltInDate = useCallback((key: string) => {
    setEnabledBuiltInDates(prev => {
      if (prev.includes(key)) return prev;
      return [...prev, key];
    });
    setHasUnsavedChanges(true);
    setShowDateDropdown(false);
  }, []);

  const handleRemoveBuiltInDate = useCallback((key: string) => {
    setEnabledBuiltInDates(prev => prev.filter(k => k !== key));
    setAnchorDates(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setHasUnsavedChanges(true);
  }, []);
  
  // Apply template state
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [applyAnchorDate, setApplyAnchorDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [currentEmployee, setCurrentEmployee] = useState<any>(null);
  
  // Fetch template data (skip if creating new template)
  const {
    data: templateData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['template', templateId],
    queryFn: () => {
      if (!templateId || templateId === 'create') throw new Error('Template ID is required');
      return TemplateService.getTemplate(templateId);
    },
    enabled: !!templateId && templateId !== 'create',
    staleTime: 30000,
  });
  
  // Fetch clients for dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: ClientService.getClients,
    staleTime: 300000,
  });
  
  // Fetch employee details for permissions
  useEffect(() => {
    if (employeeId) {
      EmployeeService.getEmployeeById(employeeId)
        .then(setCurrentEmployee)
        .catch(console.error);
    }
  }, [employeeId]);
  
  // Fetch matters/projects for apply modal
  const { data: mattersData } = useQuery({
    queryKey: ['matters', employeeId, currentEmployee],
    queryFn: async () => {
      if (!currentEmployee || !employeeId) return [];
      const permissionId = parseInt(currentEmployee.permish_id?.toString() || '0');
      if (permissionId === 1) {
        const allMatters = await MatterService.getMatters();
        return Array.isArray(allMatters) ? allMatters : [];
      } else if (permissionId === 2 || permissionId === 3) {
        const myMatters = await MatterService.getMattersByEmployee(employeeId);
        // Backend returns array directly, but type says single object - handle both
        return Array.isArray(myMatters) ? myMatters : (myMatters ? [myMatters] : []);
      }
      return [];
    },
    enabled: !!employeeId && !!currentEmployee,
    staleTime: 300000,
  });
  
  // Prepare client options
  const clientOptions: SelectOption[] = useMemo(() => {
    if (!clientsData) return [];
    return clientsData.map((client) => {
      const clientId = client.ClientID || client.clientId || '';
      const firstName = client.FirstName || client.first_name || '';
      const lastName = client.LastName || client.last_name ? ` ${client.LastName || client.last_name}` : '';
      const company = client.Company || client.company ? ` (${client.Company || client.company})` : '';
      return {
        value: clientId,
        label: `${clientId} - ${firstName}${lastName}${company}`.trim() || clientId,
      };
    });
  }, [clientsData]);
  
  // Initialize form data from template
  useEffect(() => {
    if (templateData?.template) {
      const template = templateData.template;
      const metadata = template.metadata || {};
      const tags = Array.isArray(metadata.tags) 
        ? metadata.tags.join(', ') 
        : (typeof metadata.tags === 'string' ? metadata.tags : '');
      
      setFormData({
        name: template.name || '',
        description: template.description || '',
        category: template.category || '',
        clientId: typeof template.appliesTo === 'string' 
          ? template.appliesTo 
          : (template.appliesTo as any)?.clientId || '',
        versionNote: typeof metadata.versionNote === 'string' ? metadata.versionNote : '',
        tags: tags,
      });
      const incomingAnchors = Array.isArray(metadata.customAnchors) ? metadata.customAnchors : [];
      setCustomAnchors(
        incomingAnchors.map((anchor: any, idx: number) => ({
          id: anchor.id || `anchor-${idx}-${anchor.key || anchor.label || Date.now()}`,
          key: anchor.key || slugifyAnchor(anchor.label || `anchor-${idx}`),
          label: anchor.label || anchor.key || `Anchor ${idx + 1}`,
          date: anchor.date || '',
        }))
      );
      const savedBuiltInDates = Array.isArray(metadata.enabledBuiltInDates)
        ? (metadata.enabledBuiltInDates as string[])
        : ['projectStart'];
      setEnabledBuiltInDates(savedBuiltInDates);
      setHasUnsavedChanges(false);
    }
  }, [templateData, slugifyAnchor]);
  
  // Create mutation
  const createMutation = useMutation({
    mutationFn: (payload: WorkflowTemplatePayload) => {
      return TemplateService.createTemplate(payload);
    },
    onSuccess: (data) => {
      ToastService.success('Template created successfully');
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
      // Navigate to edit page with the new template ID
      if (data.template?.templateId) {
        navigate(`/templates/${data.template.templateId}/edit`);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.message || error?.data?.error || 'Failed to create template';
      ToastService.error(errorMessage);
      
      // Check for duplicate name error
      if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('name')) {
        setValidationErrors(prev => ({
          ...prev,
          name: 'A template with this name already exists',
        }));
      }
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (payload: Partial<WorkflowTemplatePayload>) => {
      if (!templateId) throw new Error('Template ID is required');
      return TemplateService.updateTemplate(templateId, payload);
    },
    onSuccess: () => {
      ToastService.success('Template saved successfully');
      queryClient.invalidateQueries({ queryKey: ['template', templateId] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setHasUnsavedChanges(false);
      setLastSaved(new Date());
    },
    onError: (error: any) => {
      const errorMessage = error?.message || error?.data?.error || 'Failed to save template';
      ToastService.error(errorMessage);
      
      // Check for duplicate name error
      if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('name')) {
        setValidationErrors(prev => ({
          ...prev,
          name: 'A template with this name already exists',
        }));
      }
    },
  });
  
  // Handle form field changes
  const handleFieldChange = useCallback((field: keyof MetadataFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [validationErrors]);
  
  // Normalize offset rule for backend validator (accepts only absolute | business_days | relative)
  const normalizeOffsetRule = useCallback(
    (rule: OffsetRule) => {
      const allowed: Array<'absolute' | 'business_days' | 'relative'> = ['absolute', 'business_days', 'relative'];
      const anchorType = rule.offsetType;
      const offsetType = allowed.includes(anchorType as any) ? (anchorType as any) : 'relative';
      return {
        offsetType,
        offsetDays: rule.offsetDays,
        anchorType,
        referenceStepId: rule.referenceStepId,
      };
    },
    []
  );

  // Validate form
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Template name is required';
    }
    
    if (!formData.category.trim()) {
      errors.category = 'Category is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);
  
  // Build metadata object helper
  const buildMetadata = useCallback((): Record<string, unknown> => {
    const metadata: Record<string, unknown> = {};
    
    if (formData.versionNote) {
      metadata.versionNote = formData.versionNote;
    }
    
    if (formData.tags) {
      // Convert comma-separated tags to array
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      metadata.tags = tagsArray.length > 0 ? tagsArray : undefined;
    }

    if (customAnchors.length > 0) {
      metadata.customAnchors = customAnchors.map(anchor => ({
        key: anchor.key,
        label: anchor.label,
      }));
    }

    if (enabledBuiltInDates.length > 0) {
      metadata.enabledBuiltInDates = enabledBuiltInDates;
    }
    
    return metadata;
  }, [formData.versionNote, formData.tags, customAnchors, enabledBuiltInDates]);

  const buildAnchorsPayload = useCallback(() => {
    const builtIn = enabledBuiltInDates.reduce<Record<string, string>>((acc, key) => {
      if (anchorDates[key]) {
        acc[key] = anchorDates[key];
      }
      return acc;
    }, {});
    const custom = customAnchors.reduce<Record<string, string>>((acc, anchor) => {
      if (anchor.date) {
        acc[anchor.key] = anchor.date;
      }
      return acc;
    }, {});
    return { ...builtIn, ...custom };
  }, [enabledBuiltInDates, anchorDates, customAnchors]);

  const getOffsetLabel = useCallback(
    (offsetType?: string) => {
      if (!offsetType) return 'unknown';
      if (offsetType.startsWith('anchor:')) {
        const key = offsetType.replace('anchor:', '');
        const custom = customAnchors.find(a => a.key === key);
        return custom ? custom.label : `Anchor ${key}`;
      }
      return offsetType.replace(/_/g, ' ');
    },
    [customAnchors]
  );

  const getStepOffsetSummary = useCallback(
    (offsetDays: number, offsetType: string, referenceStepId?: string) => {
      const abs = Math.abs(offsetDays);
      const dir = offsetDays >= 0 ? 'after' : 'before';

      if (abs === 0) {
        let refName = '';
        if (offsetType === 'after_previous_step' && referenceStepId) {
          const allSteps = [...localSteps.map(s => ({ id: s.tempId, name: s.name })),
            ...(templateData?.steps || []).map(s => ({ id: s.stepId, name: s.name }))];
          const ref = allSteps.find(s => s.id === referenceStepId);
          refName = ref ? ref.name : 'previous step';
        } else if (offsetType.startsWith('anchor:')) {
          const key = offsetType.replace('anchor:', '');
          const custom = customAnchors.find(a => a.key === key);
          refName = custom?.label || key;
        } else {
          switch (offsetType) {
            case 'after_project_start': refName = 'Project Start'; break;
            case 'before_ifc': refName = 'IFC Date'; break;
            case 'before_construction': refName = 'Construction Start'; break;
            case 'after_previous_step': refName = 'previous step'; break;
            default: refName = offsetType.replace(/_/g, ' ');
          }
        }
        return `Same day as ${refName}`;
      }

      const months = Math.floor(abs / 30);
      const days = abs % 30;
      const parts: string[] = [];
      if (months > 0) parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
      if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);

      let refName: string;
      if (offsetType === 'after_previous_step' && referenceStepId) {
        const allSteps = [...localSteps.map(s => ({ id: s.tempId, name: s.name })),
          ...(templateData?.steps || []).map(s => ({ id: s.stepId, name: s.name }))];
        const ref = allSteps.find(s => s.id === referenceStepId);
        refName = ref ? ref.name : 'previous step';
      } else if (offsetType.startsWith('anchor:')) {
        const key = offsetType.replace('anchor:', '');
        const custom = customAnchors.find(a => a.key === key);
        refName = custom?.label || key;
      } else {
        switch (offsetType) {
          case 'after_project_start': refName = 'Project Start'; break;
          case 'before_ifc': refName = 'IFC Date'; break;
          case 'before_construction': refName = 'Construction Start'; break;
          case 'after_previous_step': refName = 'previous step'; break;
          default: refName = offsetType.replace(/_/g, ' ');
        }
      }

      return `${parts.join(' and ')} ${dir} ${refName}`;
    },
    [customAnchors, localSteps, templateData]
  );

  const loadAnchorGroups = useCallback(async () => {
    try {
      const [groupsRaw, fieldsRaw] = await Promise.all([
        CustomFieldsService.getGroupRules(),
        CustomFieldsService.getCustomFields(),
      ]);
      const groups = Array.isArray(groupsRaw) ? groupsRaw : (groupsRaw as any)?.data ?? [];
      const fields = Array.isArray(fieldsRaw) ? fieldsRaw : (fieldsRaw as any)?.data ?? [];

      const anchorFieldIds = new Set(
        fields
          .filter((f: any) => (f.type || '').toLowerCase() === 'anchor date')
          .map((f: any) => f.customfield_id)
      );
      const anchorFieldsFiltered = fields.filter((f: any) =>
        anchorFieldIds.has(f.customfield_id)
      );
      setAnchorFields(anchorFieldsFiltered);

      const parsedGroups: Array<{ id: number; name: string; fieldIds: number[] }> = [];

      (groups || []).forEach((g: any) => {
        let ids: number[] = [];
        if (Array.isArray(g.customfield_ids)) {
          ids = g.customfield_ids;
        } else if (g.assoc_customfields) {
          try {
            const parsed = typeof g.assoc_customfields === 'string'
              ? JSON.parse(g.assoc_customfields)
              : g.assoc_customfields;
            if (Array.isArray(parsed)) {
              ids = parsed.map((v: any) => Number(v)).filter((v: any) => !Number.isNaN(v));
            }
          } catch {
            // ignore parse errors
          }
        }

        const hasAnchor = ids.some((id) => anchorFieldIds.has(id));
        if (hasAnchor) {
          parsedGroups.push({
            id: g.id,
            name: g.group_name || `Group ${g.id}`,
            fieldIds: ids,
          });
        }
      });

      setAnchorGroups(parsedGroups);
    } catch (error) {
      console.error('Error loading anchor groups:', error);
      ToastService.error('Failed to load anchor groups');
    }
  }, []);

  const handleLoadAnchorGroup = useCallback(async (groupId: string) => {
    try {
      const targetId = groupId || selectedAnchorGroupId;
      if (!targetId) {
        ToastService.warning('Please select an anchor group');
        return;
      }

      const group = anchorGroups.find((g) => g.id === Number(targetId));
      if (!group || !group.fieldIds?.length) {
        ToastService.warning('No anchor fields found in this group');
        return;
      }

      const anchorFieldsFiltered = anchorFields.filter((f: any) =>
        group.fieldIds.includes(f.customfield_id)
      );

      if (!anchorFieldsFiltered.length) {
        ToastService.warning('No anchor-date type fields found in this group');
        return;
      }

      const nextAnchors = anchorFieldsFiltered.map((f: any, idx: number) => ({
        id: `anchor-import-${f.customfield_id}-${idx}`,
        key: slugifyAnchor(f.custom_field || `anchor-${f.customfield_id}`),
        label: f.custom_field || `Anchor ${idx + 1}`,
      }));

      setCustomAnchors(nextAnchors);
      ToastService.success('Loaded anchor dates from group');
    } catch (error) {
      console.error('Error loading anchor group:', error);
      ToastService.error('Failed to load anchor group');
    }
  }, [anchorGroups, anchorFields, selectedAnchorGroupId, slugifyAnchor]);

  // Ensure anchor custom fields and group rule exist
  const ensureAnchorCustomFieldsAndGroup = useCallback(
    async (templateName: string) => {
      try {
        if (!templateName) return;

        const anchors = customAnchors.map((a) => ({
          key: a.key,
          label: a.label || a.key,
        }));

        const raw = await CustomFieldsService.getCustomFields();
        const existingFields = Array.isArray(raw) ? raw : (raw as any)?.data ?? [];
        const collectedIds: number[] = [];

        for (const anchor of anchors) {
          const found = existingFields.find(
            (field: { custom_field?: string; type?: string; customfield_id: number }) =>
              field.custom_field === anchor.label &&
              (field.type || '').toLowerCase() === 'anchor date'
          );

          if (found) {
            collectedIds.push(found.customfield_id);
            continue;
          }

          const created = await CustomFieldsService.createCustomField({
            custom_field: anchor.label,
            type: 'anchor date',
          });

          if (created?.customfield_id) {
            collectedIds.push(created.customfield_id);
          }
        }

        if (collectedIds.length > 0) {
          await CustomFieldsService.createGroupRule({
            group_name: templateName,
            customfield_ids: collectedIds,
          });
        }
      } catch (error) {
        console.error('Error ensuring anchor custom fields/group rule:', error);
        ToastService.error('Failed to save anchor custom fields/group rule');
      }
    },
    [customAnchors]
  );

  // Autosave hook - saves every 10 seconds if there are unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges || !templateId || templateId === 'create') return;
    
    const timer = setTimeout(async () => {
      // Re-validate and save
      if (!validateForm()) return;
      
      // Build payload (same logic as handleSave)
      const metadata = buildMetadata();
      const existingMetadata = templateData?.template?.metadata || {};
      const mergedMetadata = { ...existingMetadata, ...metadata };
      
      const appliesTo = formData.clientId || null;
      
      const payload: Partial<WorkflowTemplatePayload> = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        category: formData.category.trim(),
        appliesTo: appliesTo as any,
        metadata: Object.keys(mergedMetadata).length > 0 ? mergedMetadata : undefined,
      };
      
      try {
        await updateMutation.mutateAsync(payload);
      } catch (error) {
        // Silently fail for autosave - don't show toast
        console.error('Autosave error:', error);
      }
    }, 10000); // 10 seconds
    
    return () => clearTimeout(timer);
  }, [formData, hasUnsavedChanges, templateId, templateData, validateForm, updateMutation, buildMetadata]);

  // Handle save (for edit mode or create with steps)
  const handleSave = useCallback(async (isAutosave = false) => {
    if (!validateForm()) {
      if (!isAutosave) {
        ToastService.warning('Please fix validation errors before saving');
      }
      return;
    }
    
    const metadata = buildMetadata();
    const appliesTo = formData.clientId || null;
    
    // Check if we're creating a new template
    if (!templateId || templateId === 'create') {
      // Convert local steps to payload format
      const stepsPayload: WorkflowTemplateStepPayload[] = localSteps.map((step, index) => {
        const normalized = normalizeOffsetRule(step.offsetRule);
        return {
          order: step.order !== undefined ? step.order : index + 1,
          name: step.name,
          category: step.category || undefined,
          discipline: step.discipline || undefined,
          role: step.role || undefined,
          offsetType: normalized.offsetType as any,
          offsetDays: normalized.offsetDays,
          metadata: {
            ...(step.description ? { description: step.description } : {}),
            anchorType: normalized.anchorType,
            referenceStepId: normalized.referenceStepId,
          },
        };
      });
      
      // Create new template with steps
      const createPayload: WorkflowTemplatePayload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        category: formData.category.trim(),
        appliesTo: appliesTo as any,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        steps: stepsPayload,
      };
      
      try {
        await createMutation.mutateAsync(createPayload);
        await ensureAnchorCustomFieldsAndGroup(formData.name.trim());
      } catch (error) {
        // Error handling is done in mutation onError
        console.error('Create error:', error);
      }
      return;
    }
    
    // Update existing template
    const updatePayload: Partial<WorkflowTemplatePayload> = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      category: formData.category.trim(),
      appliesTo: appliesTo as any,
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    };
    
    try {
      await updateMutation.mutateAsync(updatePayload);
      await ensureAnchorCustomFieldsAndGroup(formData.name.trim());
    } catch (error) {
      // Error handling is done in mutation onError
      console.error('Save error:', error);
    }
  }, [formData, validateForm, templateId, createMutation, updateMutation, localSteps, buildMetadata, normalizeOffsetRule, ensureAnchorCustomFieldsAndGroup]);
  
  // Handle save metadata only (for create mode - creates template without steps)
  const handleSaveMetadataOnly = useCallback(async () => {
    if (!validateForm()) {
      ToastService.warning('Please fix validation errors before saving');
      return;
    }
    
    if (templateId && templateId !== 'create') {
      // If editing existing template, just update metadata
      return handleSave(false);
    }
    
    const metadata = buildMetadata();
    const appliesTo = formData.clientId || null;
    
    // Create template with metadata only (no steps)
    // Steps field is optional - backend accepts None/empty/omitted
    const createPayload: any = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      category: formData.category.trim(),
      appliesTo: appliesTo as any,
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
      // Don't include steps field at all - backend will accept None/empty
    };
    
    try {
      await createMutation.mutateAsync(createPayload);
    } catch (error) {
      // Error handling is done in mutation onError
      console.error('Create metadata error:', error);
    }
  }, [formData, validateForm, templateId, createMutation, buildMetadata, handleSave]);
  
  // Handle explicit save button click
  const handleSaveClick = useCallback(() => {
    handleSave(false);
  }, [handleSave]);
  
  // Step mutations
  const createStepMutation = useMutation({
    mutationFn: (payload: CreateStepPayload) => {
      if (isCreateMode || !templateId) throw new Error('Template ID is required');
      return TemplateService.createStep(templateId, payload);
    },
    onSuccess: () => {
      ToastService.success('Step created successfully');
      queryClient.invalidateQueries({ queryKey: ['template', templateId] });
      setShowStepModal(false);
      resetStepForm();
    },
    onError: (error: any) => {
      ToastService.error(error?.message || 'Failed to create step');
    },
  });
  
  const updateStepMutation = useMutation({
    mutationFn: ({ stepId, payload }: { stepId: string; payload: UpdateStepPayload }) => {
      if (isCreateMode || !templateId) throw new Error('Template ID is required');
      return TemplateService.updateStep(templateId, stepId, payload);
    },
    onSuccess: () => {
      ToastService.success('Step updated successfully');
      queryClient.invalidateQueries({ queryKey: ['template', templateId] });
      setShowStepModal(false);
      resetStepForm();
    },
    onError: (error: any) => {
      ToastService.error(error?.message || 'Failed to update step');
    },
  });
  
  const deleteStepMutation = useMutation({
    mutationFn: (stepId: string) => {
      if (isCreateMode || !templateId) throw new Error('Template ID is required');
      return TemplateService.deleteStep(templateId, stepId);
    },
    onSuccess: () => {
      ToastService.success('Step deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['template', templateId] });
      setDeleteConfirmStep(null);
    },
    onError: (error: any) => {
      ToastService.error(error?.message || 'Failed to delete step');
    },
  });
  
  const reorderStepsMutation = useMutation({
    mutationFn: (orderedStepIds: string[]) => {
      if (isCreateMode || !templateId) throw new Error('Template ID is required');
      return TemplateService.reorderSteps(templateId, orderedStepIds);
    },
    onSuccess: () => {
      ToastService.success('Steps reordered successfully');
      queryClient.invalidateQueries({ queryKey: ['template', templateId] });
    },
    onError: (error: any) => {
      ToastService.error(error?.message || 'Failed to reorder steps');
    },
  });
  
  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!templateId || templateId === 'create') throw new Error('Template ID is required');
      return TemplateService.previewTemplate(templateId, {
        ...buildAnchorsPayload(),
      });
    },
    onSuccess: (data) => {
      setPreviewData(data);
      ToastService.success('Preview generated successfully');
    },
    onError: (error: any) => {
      ToastService.error(error?.message || 'Failed to generate preview');
    },
  });
  
  // Apply template mutation
  const applyTemplateMutation = useMutation({
    mutationFn: async (payload: WorkflowTemplateApplyPayload) => {
      return TemplateService.applyTemplate(payload);
    },
    onSuccess: (data) => {
      ToastService.success(`Template applied successfully! Created ${data.milestones.length} milestones.`);
      setShowApplyModal(false);
      // Navigate to milestones page for the project
      navigate(`/milestones?projectId=${data.instance.projectId}`);
    },
    onError: (error: any) => {
      ToastService.error(error?.message || 'Failed to apply template');
    },
  });
  
  // Reset step form
  const resetStepForm = useCallback(() => {
    setStepFormData({
      name: '',
      category: '',
      discipline: '',
      role: '',
      description: '',
      offsetRule: {
        offsetType: 'after_project_start',
        offsetDays: 0,
      },
    });
    setEditingStep(null);
    setEditingLocalStepId(null);
    setShowStepAdditional(false);
  }, []);
  
  // Handle adding step to local state (create mode)
  const handleAddLocalStep = useCallback(() => {
    const newStep = {
      tempId: `temp-${Date.now()}-${Math.random()}`,
      order: localSteps.length + 1,
      name: stepFormData.name,
      category: stepFormData.category || undefined,
      discipline: stepFormData.discipline || undefined,
      role: stepFormData.role || undefined,
      description: stepFormData.description || undefined,
      offsetRule: stepFormData.offsetRule,
    };
    setLocalSteps(prev => [...prev, newStep]);
    resetStepForm();
    setShowStepModal(false);
  }, [localSteps, stepFormData, resetStepForm]);

  // Handle updating step in local state (create mode)
  const handleUpdateLocalStep = useCallback((tempId: string) => {
    setLocalSteps(prev => prev.map(step => 
      step.tempId === tempId
        ? {
            ...step,
            name: stepFormData.name,
            category: stepFormData.category || undefined,
            discipline: stepFormData.discipline || undefined,
            role: stepFormData.role || undefined,
            description: stepFormData.description || undefined,
            offsetRule: stepFormData.offsetRule,
          }
        : step
    ));
    resetStepForm();
    setShowStepModal(false);
    setEditingStep(null);
    setEditingLocalStepId(null);
  }, [stepFormData, resetStepForm]);

  // Handle deleting step from local state (create mode)
  const handleDeleteLocalStep = useCallback((tempId: string) => {
    setLocalSteps(prev => {
      const filtered = prev.filter(step => step.tempId !== tempId);
      // Reorder remaining steps
      return filtered.map((step, index) => ({ ...step, order: index + 1 }));
    });
    setDeleteConfirmStep(null);
  }, []);

  // Handle reordering local steps (create mode)
  const handleReorderLocalSteps = useCallback((fromIndex: number, toIndex: number) => {
    setLocalSteps(prev => {
      const newSteps = [...prev];
      const [moved] = newSteps.splice(fromIndex, 1);
      newSteps.splice(toIndex, 0, moved);
      return newSteps.map((step, index) => ({ ...step, order: index + 1 }));
    });
  }, []);

  // Open step modal for create
  const handleAddStep = useCallback(() => {
    resetStepForm();

    // Default to "after the last step" if steps already exist
    if (isCreateMode) {
      if (localSteps.length > 0) {
        const lastStep = localSteps[localSteps.length - 1];
        setStepFormData(prev => ({
          ...prev,
          offsetRule: {
            offsetType: 'after_previous_step',
            offsetDays: 0,
            referenceStepId: lastStep.tempId,
          },
        }));
      }
    } else {
      const existingSteps = templateData?.steps || [];
      if (existingSteps.length > 0) {
        const sorted = [...existingSteps].sort((a, b) => (a.order || 0) - (b.order || 0));
        const lastStep = sorted[sorted.length - 1];
        setStepFormData(prev => ({
          ...prev,
          offsetRule: {
            offsetType: 'after_previous_step',
            offsetDays: 0,
            referenceStepId: lastStep.stepId,
          },
        }));
      }
    }

    setShowStepModal(true);
  }, [resetStepForm, isCreateMode, localSteps, templateData]);
  
  // Open step modal for edit
  const handleEditStep = useCallback((step: any) => {
    // Check if we're in create mode (local step) or edit mode (API step)
    if (templateId === 'create' && step.tempId) {
      // Editing a local step
      setEditingStep(null); // Use null to indicate local step
      setEditingLocalStepId(step.tempId);
      setStepFormData({
        name: step.name || '',
        category: step.category || '',
        discipline: step.discipline || '',
        role: step.role || '',
        description: step.description || '',
        offsetRule: step.offsetRule || {
          offsetType: 'after_project_start',
          offsetDays: 0,
        },
      });
      setShowStepAdditional(!!(step.category || step.discipline || step.role || step.description));
      setShowStepModal(true);
    } else {
      // Editing an API step
      const stepOrder = step.order || step.stepOrder || 0;
      const stepToEdit: WorkflowTemplateStep = {
        stepId: step.stepId,
        templateId: step.templateId,
        stepOrder: stepOrder,
        name: step.name,
        discipline: step.discipline,
        role: step.role,
        category: step.category,
        offsetType: step.offsetType,
        offsetDays: step.offsetDays,
        durationDays: step.durationDays,
        metadata: step.metadata,
        createdAt: step.createdAt,
      };
      
      setEditingStep(stepToEdit);
      setStepFormData({
        name: step.name || '',
        category: step.category || '',
        discipline: step.discipline || '',
        role: step.role || '',
        description: (step.metadata as any)?.description || '',
        offsetRule: {
          offsetType: ((step.metadata as any)?.anchorType || step.offsetType) as OffsetRule['offsetType'],
          offsetDays: step.offsetDays || 0,
          referenceStepId: (step.metadata as any)?.referenceStepId,
        },
      });
      setShowStepAdditional(!!(step.category || step.discipline || step.role || (step.metadata as any)?.description));
      setShowStepModal(true);
    }
  }, [templateId]);
  
  // Handle step form submit
  const handleStepSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stepFormData.name.trim()) {
      ToastService.warning('Step name is required');
      return;
    }
    
    // Check if we're in create mode and editing a local step
    if (isCreateMode) {
      if (editingLocalStepId) {
        // Updating existing local step
        handleUpdateLocalStep(editingLocalStepId);
      } else {
        // Adding new local step
        handleAddLocalStep();
      }
      return;
    }
    
    // Existing logic for API steps
    const normalized = normalizeOffsetRule(stepFormData.offsetRule);
    const basePayload = {
      name: stepFormData.name.trim(),
      category: stepFormData.category.trim() || undefined,
      discipline: stepFormData.discipline.trim() || undefined,
      role: stepFormData.role.trim() || undefined,
      offsetType: normalized.offsetType,
      offsetDays: normalized.offsetDays,
      metadata: {
        description: stepFormData.description.trim() || undefined,
        referenceStepId: normalized.referenceStepId,
        anchorType: normalized.anchorType,
      },
    };
    
    if (editingStep) {
      const updatePayload: UpdateStepPayload = basePayload;
      updateStepMutation.mutate({ stepId: editingStep.stepId, payload: updatePayload });
    } else {
      const createPayload: CreateStepPayload = {
        name: basePayload.name, // Required for CreateStepPayload
        category: basePayload.category,
        discipline: basePayload.discipline,
        role: basePayload.role,
        offsetType: basePayload.offsetType,
        offsetDays: basePayload.offsetDays,
        metadata: basePayload.metadata,
      };
      createStepMutation.mutate(createPayload);
    }
  }, [stepFormData, editingStep, editingLocalStepId, createStepMutation, updateStepMutation, handleAddLocalStep, handleUpdateLocalStep, isCreateMode, normalizeOffsetRule]);
  
  // Handle drag and drop
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedStepIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', ''); // Required for Firefox
  }, []);
  
  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedStepIndex === null || draggedStepIndex === dropIndex) {
      setDraggedStepIndex(null);
      return;
    }
    
    // Handle local steps (create mode)
    if (templateId === 'create') {
      handleReorderLocalSteps(draggedStepIndex, dropIndex);
      setDraggedStepIndex(null);
      return;
    }
    
    // Handle API steps (edit mode)
    const steps = templateData?.steps || [];
    const sortedSteps = [...steps].sort((a, b) => (a.order || 0) - (b.order || 0));
    const newSteps = [...sortedSteps];
    const draggedStep = newSteps[draggedStepIndex];
    newSteps.splice(draggedStepIndex, 1);
    newSteps.splice(dropIndex, 0, draggedStep);
    
    // Update step orders and reorder via API
    const orderedStepIds = newSteps.map(step => step.stepId);
    reorderStepsMutation.mutate(orderedStepIds);
    setDraggedStepIndex(null);
  }, [draggedStepIndex, templateId, templateData, reorderStepsMutation, handleReorderLocalSteps]);
  
  const handleDragEnd = useCallback(() => {
    setDraggedStepIndex(null);
  }, []);
  
  // Handle delete step
  const handleDeleteStep = useCallback((stepId: string) => {
    setDeleteConfirmStep(stepId);
  }, []);
  
  const confirmDeleteStep = useCallback(() => {
    if (!deleteConfirmStep) return;
    
    if (isCreateMode && deleteConfirmStep.startsWith('temp-')) {
      handleDeleteLocalStep(deleteConfirmStep);
    } else {
      deleteStepMutation.mutate(deleteConfirmStep);
    }
  }, [deleteConfirmStep, isCreateMode, deleteStepMutation, handleDeleteLocalStep]);
  
  // Render active tab content
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'create':
        return renderCreateTab();
      case 'preview':
        return renderPreviewTab();
      default:
        return renderCreateTab();
    }
  };
  
  // Render create tab (combines metadata and steps)
  const renderCreateTab = () => {
    // Handle create mode - show the actual form with steps
    if (isCreateMode) {
      return (
        <div className="metadata-form">
          <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
            {/* Hero Name Field */}
            <div className="detail-hero">
              <div className="detail-hero-top">
                <input
                  type="text"
                  className={`detail-hero-input ${validationErrors.name ? 'error' : ''}`}
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="Untitled Template"
                />
                <div className="save-status">
                  {hasUnsavedChanges && (
                    <span className="unsaved-indicator">Unsaved</span>
                  )}
                  {localSteps.length > 0 && (
                    <span className="saved-indicator">
                      {localSteps.length} step{localSteps.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveClick}
                    disabled={createMutation.isPending || !formData.name.trim() || !formData.category.trim()}
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Template'}
                  </button>
                </div>
              </div>
              {validationErrors.name && (
                <span className="error-message">{validationErrors.name}</span>
              )}
            </div>

            {/* Properties Section */}
            <div className="detail-section">
              <div className="detail-section-title">Properties</div>
              <div className="detail-grid">
                <div className="detail-field">
                  <label className="detail-field-label">Category <span className="detail-required">*</span></label>
                  <input
                    type="text"
                    className={`form-input ${validationErrors.category ? 'error' : ''}`}
                    value={formData.category}
                    onChange={(e) => handleFieldChange('category', e.target.value)}
                    placeholder="e.g., Construction, Design"
                  />
                  {validationErrors.category && (
                    <span className="error-message">{validationErrors.category}</span>
                  )}
                </div>
                <div className="detail-field">
                  <label className="detail-field-label">Client</label>
                  <select
                    className="form-input"
                    value={formData.clientId}
                    onChange={(e) => handleFieldChange('clientId', e.target.value)}
                  >
                    <option value="">All Clients</option>
                    {clientOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="detail-field">
                  <label className="detail-field-label">Version Note</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.versionNote}
                    onChange={(e) => handleFieldChange('versionNote', e.target.value)}
                    placeholder="e.g., v2.0 — 2026 update"
                  />
                </div>
                <div className="detail-field">
                  <label className="detail-field-label">Tags</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.tags}
                    onChange={(e) => handleFieldChange('tags', e.target.value)}
                    placeholder="comma separated"
                  />
                </div>
              </div>
              <div className="detail-field detail-desc-field">
                <label className="detail-field-label">Description</label>
                <textarea
                  className="form-input"
                  value={formData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Add a description..."
                  rows={3}
                />
              </div>
            </div>

            <div className="custom-anchor-section">
              <div
                className={`section-header-toggle ${showKeyDatesSection ? 'expanded' : ''}`}
                onClick={() => setShowKeyDatesSection(!showKeyDatesSection)}
              >
                <div className="section-toggle-top">
                  <div className="section-toggle-title-row">
                    <span className={`section-chevron ${showKeyDatesSection ? 'expanded' : ''}`}>&#9656;</span>
                    <h3 className="section-toggle-title">Key Project Dates</h3>
                    {!showKeyDatesSection && (
                      <span className="section-inline-hint">
                        {enabledBuiltInDates.length + customAnchors.length > 0
                          ? `${enabledBuiltInDates.length + customAnchors.length} date${enabledBuiltInDates.length + customAnchors.length !== 1 ? 's' : ''} configured`
                          : 'Click to add'}
                      </span>
                    )}
                  </div>
                  {showKeyDatesSection && (
                    <p className="section-toggle-desc">
                      Set the reference dates your workflow revolves around. Each step can be scheduled relative to these dates.
                    </p>
                  )}
                </div>
              </div>

              {showKeyDatesSection && (
                <>
                  <div className="section-actions-row">
                    <div className="anchor-load-controls">
                      <select
                        className="form-input"
                        value={selectedAnchorGroupId}
                        onChange={(e) => {
                          setSelectedAnchorGroupId(e.target.value);
                          handleLoadAnchorGroup(e.target.value);
                        }}
                        onFocus={loadAnchorGroups}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">Import from date group</option>
                        {anchorGroups.map((g) => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="date-add-controls">
                    <div className="date-dropdown-wrapper">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowDateDropdown(!showDateDropdown)}
                      >
                        + Add Key Date
                      </button>
                      {showDateDropdown && (
                        <>
                          <div className="date-dropdown-backdrop" onClick={() => setShowDateDropdown(false)} />
                          <div className="date-dropdown-menu">
                            {BUILT_IN_DATES.filter(d => !enabledBuiltInDates.includes(d.key)).length > 0 && (
                              <div className="date-dropdown-group-label">Built-in Dates</div>
                            )}
                            {BUILT_IN_DATES.filter(d => !enabledBuiltInDates.includes(d.key)).map(d => (
                              <button
                                key={d.key}
                                type="button"
                                className="date-dropdown-item"
                                onClick={() => handleAddBuiltInDate(d.key)}
                              >
                                {d.label}
                              </button>
                            ))}
                            <div className="date-dropdown-divider" />
                            <div className="date-dropdown-group-label">Custom Date</div>
                            <div className="date-dropdown-custom">
                              <input
                                type="text"
                                className="form-input"
                                value={newAnchorName}
                                onChange={(e) => setNewAnchorName(e.target.value)}
                                placeholder="Custom date name..."
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddCustomAnchor();
                                    if (newAnchorName.trim()) setShowDateDropdown(false);
                                  }
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                  handleAddCustomAnchor();
                                  if (newAnchorName.trim()) setShowDateDropdown(false);
                                }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="custom-anchor-list">
                    {enabledBuiltInDates.map(key => {
                      const dateInfo = BUILT_IN_DATES.find(d => d.key === key);
                      return (
                        <div key={key} className="custom-anchor-row">
                          <div className="custom-anchor-info">
                            <div className="anchor-label">{dateInfo?.label || key}</div>
                            <div className="anchor-key">Key: {key}</div>
                          </div>
                          <div className="custom-anchor-controls">
                            <span className="anchor-badge">Built-in</span>
                            <button
                              type="button"
                              className="btn btn-icon"
                              onClick={() => handleRemoveBuiltInDate(key)}
                              title="Remove date"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {customAnchors.map(anchor => (
                      <div key={anchor.id} className="custom-anchor-row">
                        <div className="custom-anchor-info">
                          <div className="anchor-label">{anchor.label}</div>
                          <div className="anchor-key">Key: {anchor.key}</div>
                        </div>
                        <div className="custom-anchor-controls">
                          <span className="anchor-badge">Custom</span>
                          <button
                            type="button"
                            className="btn btn-icon"
                            onClick={() => handleRemoveCustomAnchor(anchor.id)}
                            title="Remove date"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}

                    {enabledBuiltInDates.length === 0 && customAnchors.length === 0 && (
                      <div className="custom-anchor-empty">
                        <p>No key dates added yet. Click "+ Add Key Date" to define reference dates for your workflow.</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/templates')}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSaveMetadataOnly}
                disabled={createMutation.isPending || !hasUnsavedChanges}
              >
                {createMutation.isPending ? 'Saving...' : 'Save Metadata Only'}
              </button>
            </div>
            
            <div className="create-tab-steps-section">
              <div className="create-tab-steps-header">
                <label className="form-label" style={{ marginBottom: 0, fontSize: 'var(--font-size-sm)' }}>
                  Workflow Steps
                </label>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddStep}
                >
                  + Add Step
                </button>
              </div>
              
              {localSteps.length === 0 ? (
                <div className="steps-empty-state">
                  <p>No steps yet. Add your first step to get started.</p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddStep}
                  >
                    Add Step
                  </button>
                </div>
              ) : (
                <div className="steps-list">
                  {localSteps.map((step, index) => {
                    // Calculate step number: use step.order if valid, otherwise use index + 1
                    const stepNumber = (step.order && step.order > 0) ? step.order : index + 1;
                    return (
                    <div
                      key={step.tempId}
                      className={`step-item ${draggedStepIndex === index ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (draggedStepIndex !== null && draggedStepIndex !== index) {
                          handleReorderLocalSteps(draggedStepIndex, index);
                        }
                        setDraggedStepIndex(null);
                      }}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="step-drag-handle">☰</div>
                      <div className="step-content">
                        <div className="step-header">
                          <span className="step-order">Step {stepNumber}</span>
                          <h3 className="step-name">{step.name}</h3>
                        </div>
                        <div className="step-details">
                          {step.category && <span className="step-badge">{step.category}</span>}
                          {step.discipline && <span className="step-badge">{step.discipline}</span>}
                          {step.role && <span className="step-badge">{step.role}</span>}
                        </div>
                        <div className="step-offset">
                          <small>
                            {getStepOffsetSummary(step.offsetRule.offsetDays, step.offsetRule.offsetType, step.offsetRule.referenceStepId)}
                          </small>
                        </div>
                      </div>
                      <div className="step-actions" onMouseDown={(e) => e.stopPropagation()} onDragStart={(e) => e.preventDefault()}>
                        <button
                          className="btn-icon"
                          onClick={() => handleEditStep({ ...step, tempId: step.tempId })}
                          title="Edit step"
                          type="button"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleDeleteStep(step.tempId)}
                          title="Delete step"
                          type="button"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
            
          </form>

        {/* Step Form Modal (create mode) */}
        {showStepModal && (
          <div className="modal-overlay" onClick={() => { setShowStepModal(false); resetStepForm(); }}>
            <div className="modal-content step-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingStep ? 'Edit Step' : 'Add Step'}</h2>
                <button
                  className="btn-icon"
                  onClick={() => { setShowStepModal(false); resetStepForm(); }}
                >
                  ✕
                </button>
              </div>
              <form onSubmit={handleStepSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label className="form-label required">Step Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={stepFormData.name}
                      onChange={(e) => setStepFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter step name"
                      required
                    />
                  </div>

                  <OffsetRuleEditor
                    value={stepFormData.offsetRule}
                    onChange={(rule) => setStepFormData(prev => ({ ...prev, offsetRule: rule }))}
                    availableSteps={
                      isCreateMode
                        ? localSteps.map(s => ({
                            stepId: s.tempId,
                            name: s.name,
                            stepOrder: s.order,
                          }))
                        : (templateData?.steps || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map(s => ({
                            stepId: s.stepId,
                            name: s.name,
                            stepOrder: s.order || 0,
                          }))
                    }
                    currentStepId={isCreateMode ? editingLocalStepId || undefined : editingStep?.stepId}
                    customAnchors={customAnchors.map(a => ({ key: a.key, label: a.label }))}
                  />

                  <div className="step-additional-toggle" onClick={() => setShowStepAdditional(!showStepAdditional)}>
                    <span className={`section-chevron ${showStepAdditional ? 'expanded' : ''}`}>&#9656;</span>
                    <span className="step-additional-label">Additional Information</span>
                    {!showStepAdditional && (stepFormData.category || stepFormData.discipline || stepFormData.role || stepFormData.description) && (
                      <span className="step-additional-hint">filled</span>
                    )}
                  </div>
                  {showStepAdditional && (
                    <div className="step-additional-fields">
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Category</label>
                          <input
                            type="text"
                            className="form-input"
                            value={stepFormData.category}
                            onChange={(e) => setStepFormData(prev => ({ ...prev, category: e.target.value }))}
                            placeholder="e.g., Review, Approval"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Discipline</label>
                          <input
                            type="text"
                            className="form-input"
                            value={stepFormData.discipline}
                            onChange={(e) => setStepFormData(prev => ({ ...prev, discipline: e.target.value }))}
                            placeholder="e.g., Mechanical, Electrical"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Role</label>
                        <input
                          type="text"
                          className="form-input"
                          value={stepFormData.role}
                          onChange={(e) => setStepFormData(prev => ({ ...prev, role: e.target.value }))}
                          placeholder="e.g., Project Manager, Engineer"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-input"
                          value={stepFormData.description}
                          onChange={(e) => setStepFormData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Enter step description"
                          rows={3}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { setShowStepModal(false); resetStepForm(); }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={createStepMutation.isPending || updateStepMutation.isPending}
                  >
                    {createStepMutation.isPending || updateStepMutation.isPending
                      ? 'Saving...'
                      : editingStep
                      ? 'Update Step'
                      : 'Create Step'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Delete Confirmation Modal (create mode local steps) */}
        {deleteConfirmStep && isCreateMode && (
          <div className="modal-overlay" onClick={() => setDeleteConfirmStep(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Delete Step</h2>
                <button
                  className="btn-icon"
                  onClick={() => setDeleteConfirmStep(null)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this step? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDeleteConfirmStep(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmDeleteStep}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      );
    }
    
    // Edit mode - show metadata form on top, then steps below
    if (isLoading) {
      return <div className="loading-state">Loading template...</div>;
    }
    
    if (error) {
      return (
        <div className="error-state">
          <p>Error loading template: {error instanceof Error ? error.message : 'Unknown error'}</p>
          <button className="btn btn-primary" onClick={() => refetch()}>Retry</button>
        </div>
      );
    }
    
    const steps = templateData?.steps || [];
    const sortedSteps = [...steps].sort((a, b) => (a.order || 0) - (b.order || 0));
    
    return (
      <div className="create-tab">
        {/* Metadata Section */}
        <div className="metadata-form">
          <form onSubmit={(e) => { e.preventDefault(); handleSaveClick(); }}>
            {/* Hero Name Field */}
            <div className="detail-hero">
              <div className="detail-hero-top">
                <input
                  type="text"
                  className={`detail-hero-input ${validationErrors.name ? 'error' : ''}`}
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="Untitled Template"
                />
                <div className="save-status">
                  {hasUnsavedChanges && (
                    <span className="unsaved-indicator">Unsaved</span>
                  )}
                  {lastSaved && !hasUnsavedChanges && (
                    <span className="saved-indicator">
                      Saved {lastSaved.toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
              {validationErrors.name && (
                <span className="error-message">{validationErrors.name}</span>
              )}
            </div>

            {/* Properties Section */}
            <div className="detail-section">
              <div className="detail-section-title">Properties</div>
              <div className="detail-grid">
                <div className="detail-field">
                  <label className="detail-field-label">Category <span className="detail-required">*</span></label>
                  <input
                    type="text"
                    className={`form-input ${validationErrors.category ? 'error' : ''}`}
                    value={formData.category}
                    onChange={(e) => handleFieldChange('category', e.target.value)}
                    placeholder="e.g., Construction, Design"
                  />
                  {validationErrors.category && (
                    <span className="error-message">{validationErrors.category}</span>
                  )}
                </div>
                <div className="detail-field">
                  <label className="detail-field-label">Client</label>
                  <select
                    className="form-input"
                    value={formData.clientId}
                    onChange={(e) => handleFieldChange('clientId', e.target.value)}
                  >
                    <option value="">All Clients</option>
                    {clientOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="detail-field">
                  <label className="detail-field-label">Version Note</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.versionNote}
                    onChange={(e) => handleFieldChange('versionNote', e.target.value)}
                    placeholder="e.g., v2.0 — 2026 update"
                  />
                </div>
                <div className="detail-field">
                  <label className="detail-field-label">Tags</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.tags}
                    onChange={(e) => handleFieldChange('tags', e.target.value)}
                    placeholder="comma separated"
                  />
                </div>
              </div>
              <div className="detail-field detail-desc-field">
                <label className="detail-field-label">Description</label>
                <textarea
                  className="form-input"
                  value={formData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Add a description..."
                  rows={3}
                />
              </div>
            </div>

            {/* Key Project Dates */}
            <div className="custom-anchor-section">
              <div
                className={`section-header-toggle ${showKeyDatesSection ? 'expanded' : ''}`}
                onClick={() => setShowKeyDatesSection(!showKeyDatesSection)}
              >
                <div className="section-toggle-top">
                  <div className="section-toggle-title-row">
                    <span className={`section-chevron ${showKeyDatesSection ? 'expanded' : ''}`}>&#9656;</span>
                    <h3 className="section-toggle-title">Key Project Dates</h3>
                    {!showKeyDatesSection && (
                      <span className="section-inline-hint">
                        {enabledBuiltInDates.length + customAnchors.length > 0
                          ? `${enabledBuiltInDates.length + customAnchors.length} date${enabledBuiltInDates.length + customAnchors.length !== 1 ? 's' : ''} configured`
                          : 'Click to add'}
                      </span>
                    )}
                  </div>
                  {showKeyDatesSection && (
                    <p className="section-toggle-desc">
                      Set the reference dates your workflow revolves around. Each step can be scheduled relative to these dates.
                    </p>
                  )}
                </div>
              </div>

              {showKeyDatesSection && (
                <>
                  <div className="section-actions-row">
                    <div className="anchor-load-controls">
                      <select
                        className="form-input"
                        value={selectedAnchorGroupId}
                        onChange={(e) => {
                          setSelectedAnchorGroupId(e.target.value);
                          handleLoadAnchorGroup(e.target.value);
                        }}
                        onFocus={loadAnchorGroups}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">Import from date group</option>
                        {anchorGroups.map((g) => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="date-add-controls">
                    <div className="date-dropdown-wrapper">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowDateDropdown(!showDateDropdown)}
                      >
                        + Add Key Date
                      </button>
                      {showDateDropdown && (
                        <>
                          <div className="date-dropdown-backdrop" onClick={() => setShowDateDropdown(false)} />
                          <div className="date-dropdown-menu">
                            {BUILT_IN_DATES.filter(d => !enabledBuiltInDates.includes(d.key)).length > 0 && (
                              <div className="date-dropdown-group-label">Built-in Dates</div>
                            )}
                            {BUILT_IN_DATES.filter(d => !enabledBuiltInDates.includes(d.key)).map(d => (
                              <button
                                key={d.key}
                                type="button"
                                className="date-dropdown-item"
                                onClick={() => handleAddBuiltInDate(d.key)}
                              >
                                {d.label}
                              </button>
                            ))}
                            <div className="date-dropdown-divider" />
                            <div className="date-dropdown-group-label">Custom Date</div>
                            <div className="date-dropdown-custom">
                              <input
                                type="text"
                                className="form-input"
                                value={newAnchorName}
                                onChange={(e) => setNewAnchorName(e.target.value)}
                                placeholder="Custom date name..."
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddCustomAnchor();
                                    if (newAnchorName.trim()) setShowDateDropdown(false);
                                  }
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                  handleAddCustomAnchor();
                                  if (newAnchorName.trim()) setShowDateDropdown(false);
                                }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="custom-anchor-list">
                    {enabledBuiltInDates.map(key => {
                      const dateInfo = BUILT_IN_DATES.find(d => d.key === key);
                      return (
                        <div key={key} className="custom-anchor-row">
                          <div className="custom-anchor-info">
                            <div className="anchor-label">{dateInfo?.label || key}</div>
                            <div className="anchor-key">Key: {key}</div>
                          </div>
                          <div className="custom-anchor-controls">
                            <span className="anchor-badge">Built-in</span>
                            <button
                              type="button"
                              className="btn btn-icon"
                              onClick={() => handleRemoveBuiltInDate(key)}
                              title="Remove date"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {customAnchors.map(anchor => (
                      <div key={anchor.id} className="custom-anchor-row">
                        <div className="custom-anchor-info">
                          <div className="anchor-label">{anchor.label}</div>
                          <div className="anchor-key">Key: {anchor.key}</div>
                        </div>
                        <div className="custom-anchor-controls">
                          <span className="anchor-badge">Custom</span>
                          <button
                            type="button"
                            className="btn btn-icon"
                            onClick={() => handleRemoveCustomAnchor(anchor.id)}
                            title="Remove date"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}

                    {enabledBuiltInDates.length === 0 && customAnchors.length === 0 && (
                      <div className="custom-anchor-empty">
                        <p>No key dates added yet. Click "+ Add Key Date" to define reference dates for your workflow.</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/templates')}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={(updateMutation.isPending || createMutation.isPending) || !hasUnsavedChanges}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
        
        {/* Steps Section */}
        <div className="steps-tab edit-steps-divider">
          <div className="steps-header">
            <h2>Workflow Steps</h2>
            <button
              className="btn btn-primary"
              onClick={handleAddStep}
            >
              + Add Step
            </button>
          </div>
          
          {sortedSteps.length === 0 ? (
            <div className="empty-state">
              <p>No steps yet. Add your first step to get started.</p>
              <button className="btn btn-primary" onClick={handleAddStep}>
                Add Step
              </button>
            </div>
          ) : (
            <div className="steps-list">
              {sortedSteps.map((step, index) => {
                // Calculate step number: use step.order if available, otherwise use index + 1
                const stepNumber = step.order || (index + 1);
                return (
                <div
                  key={step.stepId}
                  className={`step-item ${draggedStepIndex === index ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="step-drag-handle">☰</div>
                  <div className="step-content">
                    <div className="step-header">
                      <span className="step-order">Step {stepNumber}</span>
                      <h3 className="step-name">{step.name}</h3>
                    </div>
                    <div className="step-details">
                      {step.category && <span className="step-badge">{step.category}</span>}
                      {step.discipline && <span className="step-badge">{step.discipline}</span>}
                      {step.role && <span className="step-badge">{step.role}</span>}
                    </div>
                    <div className="step-offset">
                      <small>
                        {getStepOffsetSummary(step.offsetDays, (step.metadata as any)?.anchorType || step.offsetType, (step.metadata as any)?.referenceStepId)}
                      </small>
                    </div>
                  </div>
                  <div className="step-actions" onMouseDown={(e) => e.stopPropagation()} onDragStart={(e) => e.preventDefault()}>
                    <button
                      className="btn-icon"
                      onClick={() => handleEditStep(step)}
                      title="Edit step"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDeleteStep(step.stepId)}
                      title="Delete step"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Step Form Modal */}
        {showStepModal && (
          <div className="modal-overlay" onClick={() => { setShowStepModal(false); resetStepForm(); }}>
            <div className="modal-content step-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingStep ? 'Edit Step' : 'Add Step'}</h2>
                <button
                  className="btn-icon"
                  onClick={() => { setShowStepModal(false); resetStepForm(); }}
                >
                  ✕
                </button>
              </div>
              <form onSubmit={handleStepSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label className="form-label required">Step Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={stepFormData.name}
                      onChange={(e) => setStepFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter step name"
                      required
                    />
                  </div>

                  <OffsetRuleEditor
                    value={stepFormData.offsetRule}
                    onChange={(rule) => setStepFormData(prev => ({ ...prev, offsetRule: rule }))}
                    availableSteps={
                      templateId === 'create'
                        ? localSteps.map(s => ({
                            stepId: s.tempId,
                            name: s.name,
                            stepOrder: s.order,
                          }))
                        : (templateData?.steps || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map(s => ({
                            stepId: s.stepId,
                            name: s.name,
                            stepOrder: s.order || 0,
                          }))
                    }
                    currentStepId={templateId === 'create' ? editingLocalStepId || undefined : editingStep?.stepId}
                    customAnchors={customAnchors.map(a => ({ key: a.key, label: a.label }))}
                  />

                  <div className="step-additional-toggle" onClick={() => setShowStepAdditional(!showStepAdditional)}>
                    <span className={`section-chevron ${showStepAdditional ? 'expanded' : ''}`}>&#9656;</span>
                    <span className="step-additional-label">Additional Information</span>
                    {!showStepAdditional && (stepFormData.category || stepFormData.discipline || stepFormData.role || stepFormData.description) && (
                      <span className="step-additional-hint">filled</span>
                    )}
                  </div>
                  {showStepAdditional && (
                    <div className="step-additional-fields">
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Category</label>
                          <input
                            type="text"
                            className="form-input"
                            value={stepFormData.category}
                            onChange={(e) => setStepFormData(prev => ({ ...prev, category: e.target.value }))}
                            placeholder="e.g., Review, Approval"
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Discipline</label>
                          <input
                            type="text"
                            className="form-input"
                            value={stepFormData.discipline}
                            onChange={(e) => setStepFormData(prev => ({ ...prev, discipline: e.target.value }))}
                            placeholder="e.g., Mechanical, Electrical"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Role</label>
                        <input
                          type="text"
                          className="form-input"
                          value={stepFormData.role}
                          onChange={(e) => setStepFormData(prev => ({ ...prev, role: e.target.value }))}
                          placeholder="e.g., Project Manager, Engineer"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-input"
                          value={stepFormData.description}
                          onChange={(e) => setStepFormData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Enter step description"
                          rows={3}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { setShowStepModal(false); resetStepForm(); }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={createStepMutation.isPending || updateStepMutation.isPending}
                  >
                    {createStepMutation.isPending || updateStepMutation.isPending
                      ? 'Saving...'
                      : editingStep
                      ? 'Update Step'
                      : 'Create Step'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Delete Confirmation Modal */}
        {deleteConfirmStep && (
          <div className="modal-overlay" onClick={() => setDeleteConfirmStep(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Delete Step</h2>
                <button
                  className="btn-icon"
                  onClick={() => setDeleteConfirmStep(null)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this step? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDeleteConfirmStep(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmDeleteStep}
                  disabled={deleteStepMutation.isPending}
                >
                  {deleteStepMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  const [hoveredBarId, setHoveredBarId] = useState<string | null>(null);

  const previewSummary = useMemo(() => {
    if (!ganttData || !previewData) return null;
    const { bars, minDate, maxDate, totalDays, disciplines } = ganttData;
    const milestoneCount = bars.filter(b => b.isMilestone).length;
    const categories = Array.from(new Set(bars.map(b => b.category).filter(Boolean))) as string[];
    const overdueSteps = bars.filter(b => b.end < new Date());
    return {
      totalSteps: bars.length,
      milestoneCount,
      taskCount: bars.length - milestoneCount,
      totalDays,
      dateRange: `${format(minDate, 'MMM dd, yyyy')} — ${format(maxDate, 'MMM dd, yyyy')}`,
      disciplines,
      categories,
      overdueCount: overdueSteps.length,
    };
  }, [ganttData, previewData]);

  // Render preview tab
  const renderPreviewTab = () => {
    const handleGeneratePreview = () => {
      if (isCreateMode) {
        // Unsaved template: compute the timeline locally instead of calling the
        // preview endpoint (which needs a saved template). The compute mirrors
        // the backend — parity pinned by shared/template-preview-parity.json.
        if (localSteps.length === 0) {
          ToastService.warning('Add at least one step before generating a preview.');
          return;
        }
        try {
          setPreviewData(computeLocalTemplatePreview({
            templateName: formData.name,
            // Adapt localSteps to the saved-row shape the backend previews:
            // normalizeOffsetRule splits the UI rule into the stored offsetType
            // + metadata anchorType exactly as the create payload does, so this
            // preview matches what save-then-server-preview would return.
            // (No durationDays field exists locally — steps preview milestone-style.)
            steps: localSteps.map((step, index) => {
              const normalized = normalizeOffsetRule(step.offsetRule);
              return {
                stepId: step.tempId,
                stepOrder: step.order !== undefined ? step.order : index + 1,
                name: step.name,
                discipline: step.discipline || null,
                category: step.category || null,
                offsetType: normalized.offsetType,
                offsetDays: normalized.offsetDays,
                durationDays: null,
                anchorType: normalized.anchorType,
              };
            }),
            anchors: buildAnchorsPayload(),
          }));
          ToastService.success('Preview generated successfully');
        } catch (error: any) {
          ToastService.error(error?.message || 'Failed to generate preview');
        }
        return;
      }
      previewMutation.mutate();
    };

    const handleExportPDF = () => {
      if (!previewData) return;
      try {
        const pdf = new jsPDF('landscape', 'mm', 'a4');
        const tplName = templateData?.template?.name || formData.name || 'template';
        pdf.setFontSize(16);
        pdf.text(`Timeline Preview: ${tplName}`, 14, 15);

        pdf.setFontSize(10);
        let yPos = 25;
        enabledBuiltInDates.forEach(key => {
          const dateInfo = BUILT_IN_DATES.find(d => d.key === key);
          const dateValue = anchorDates[key];
          if (dateValue) {
            pdf.text(`${dateInfo?.label || key}: ${format(parseISO(dateValue), 'MMM dd, yyyy')}`, 14, yPos);
            yPos += 7;
          }
        });
        customAnchors.forEach((anchor) => {
          if (!anchor.date) return;
          pdf.text(`${anchor.label}: ${format(parseISO(anchor.date), 'MMM dd, yyyy')}`, 14, yPos);
          yPos += 7;
        });
        yPos += 3;

        if (previewData.warnings && previewData.warnings.length > 0) {
          pdf.setFontSize(12);
          pdf.text('Warnings:', 14, yPos);
          yPos += 7;
          pdf.setFontSize(10);
          previewData.warnings.forEach((warning) => {
            const lines = pdf.splitTextToSize(`• ${warning}`, 270);
            lines.forEach((line: string) => {
              if (yPos > 190) { pdf.addPage(); yPos = 15; }
              pdf.text(line, 20, yPos);
              yPos += 7;
            });
          });
          yPos += 5;
        }

        pdf.setFontSize(12);
        pdf.text('Timeline:', 14, yPos);
        yPos += 7;
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Step', 14, yPos);
        pdf.text('Discipline', 80, yPos);
        pdf.text('Planned Date', 130, yPos);
        pdf.text('Duration', 180, yPos);
        pdf.text('Offset', 210, yPos);
        pdf.text('Reference', 245, yPos);
        yPos += 7;
        pdf.setFont('helvetica', 'normal');

        previewData.timeline.forEach((item) => {
          if (yPos > 190) { pdf.addPage(); yPos = 15; }
          const stepDisplayName = item.stepName || (item as any).name || 'Untitled Step';
          const stepName = pdf.splitTextToSize(stepDisplayName, 60);
          const plannedDate = format(parseISO(item.plannedDate), 'MMM dd, yyyy');
          const endDate = item.endDate ? parseISO(item.endDate) : parseISO(item.plannedDate);
          const dur = differenceInCalendarDays(endDate, parseISO(item.plannedDate));
          const offset = `${item.offsetDays >= 0 ? '+' : ''}${item.offsetDays}d`;

          pdf.text(stepName[0], 14, yPos);
          pdf.text(item.discipline || '—', 80, yPos);
          pdf.text(plannedDate, 130, yPos);
          pdf.text(dur > 0 ? `${dur} days` : 'Milestone', 180, yPos);
          pdf.text(offset, 210, yPos);
          pdf.text(getOffsetLabel(item.offsetType), 245, yPos);
          yPos += 7;
        });

        pdf.save(`${tplName}-preview.pdf`);
        ToastService.success('PDF exported successfully');
      } catch (error) {
        console.error('Export error:', error);
        ToastService.error('Failed to export PDF');
      }
    };

    const handleExportPNG = () => {
      if (!previewData || previewViewMode !== 'chart') {
        ToastService.warning('Please switch to Chart View to export as PNG');
        return;
      }
      ToastService.warning('PNG export is not available for the Gantt chart view yet.');
    };

    const renderSummaryStats = () => {
      if (!previewSummary) return null;
      return (
        <div className="pv-summary-stats">
          <div className="pv-stat-card">
            <div className="pv-stat-value">{previewSummary.totalSteps}</div>
            <div className="pv-stat-label">Total Steps</div>
          </div>
          <div className="pv-stat-card">
            <div className="pv-stat-value">{previewSummary.totalDays}</div>
            <div className="pv-stat-label">Total Days</div>
          </div>
          <div className="pv-stat-card">
            <div className="pv-stat-value">{previewSummary.milestoneCount}</div>
            <div className="pv-stat-label">Milestones</div>
          </div>
          <div className="pv-stat-card">
            <div className="pv-stat-value">{previewSummary.disciplines.length}</div>
            <div className="pv-stat-label">Disciplines</div>
          </div>
          <div className="pv-stat-card pv-stat-card--wide">
            <div className="pv-stat-value pv-stat-value--sm">{previewSummary.dateRange}</div>
            <div className="pv-stat-label">Date Range</div>
          </div>
        </div>
      );
    };

    const renderDisciplineLegend = () => {
      if (!ganttData || ganttData.disciplines.length === 0) return null;
      return (
        <div className="pv-discipline-legend">
          {ganttData.disciplines.map(d => {
            const color = getDisciplineColor(d);
            return (
              <span key={d} className="pv-legend-item">
                <span className="pv-legend-dot" style={{ background: color.bar }} />
                {d}
              </span>
            );
          })}
          {ganttData.bars.some(b => !b.discipline) && (
            <span className="pv-legend-item">
              <span className="pv-legend-dot" style={{ background: DISCIPLINE_COLORS['_default'].bar }} />
              Unassigned
            </span>
          )}
        </div>
      );
    };

    const renderEnhancedListView = () => {
      if (!previewData) return null;
      const timeline = [...previewData.timeline].sort((a, b) => {
        return parseISO(a.plannedDate).getTime() - parseISO(b.plannedDate).getTime();
      });

      return (
        <div className="pv-enhanced-list">
          {timeline.map((item, idx) => {
            const startDate = parseISO(item.plannedDate);
            const endDate = item.endDate ? parseISO(item.endDate) : startDate;
            const durationDays = differenceInCalendarDays(endDate, startDate);
            const isMilestone = durationDays === 0;
            const color = getDisciplineColor(item.discipline);
            const stepName = item.stepName || (item as any).name || 'Untitled Step';

            let posLeft = 0;
            let posWidth = 100;
            if (ganttData) {
              const startOff = differenceInCalendarDays(startDate, ganttData.minDate);
              const endOff = differenceInCalendarDays(endDate, ganttData.minDate);
              posLeft = (startOff / ganttData.totalDays) * 100;
              posWidth = isMilestone ? 1 : (Math.max(1, endOff - startOff + 1) / ganttData.totalDays) * 100;
            }

            return (
              <div key={item.stepId} className="pv-list-card" style={{ borderLeftColor: color.bar }}>
                <div className="pv-list-card-header">
                  <div className="pv-list-card-order">{idx + 1}</div>
                  <div className="pv-list-card-title">{stepName}</div>
                  {isMilestone && <span className="pv-milestone-badge">Milestone</span>}
                  {item.discipline && (
                    <span className="pv-discipline-tag" style={{ background: color.bg, color: color.fg }}>
                      {item.discipline}
                    </span>
                  )}
                  {item.category && (
                    <span className="pv-category-tag">{item.category}</span>
                  )}
                </div>
                <div className="pv-list-card-body">
                  <div className="pv-list-card-details">
                    <div className="pv-detail-item">
                      <span className="pv-detail-label">Date</span>
                      <span className="pv-detail-value">
                        {format(startDate, 'MMM dd, yyyy')}
                        {!isMilestone && ` — ${format(endDate, 'MMM dd, yyyy')}`}
                      </span>
                    </div>
                    {!isMilestone && (
                      <div className="pv-detail-item">
                        <span className="pv-detail-label">Duration</span>
                        <span className="pv-detail-value">{durationDays} day{durationDays !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                    <div className="pv-detail-item">
                      <span className="pv-detail-label">Offset</span>
                      <span className="pv-detail-value">
                        {item.offsetDays >= 0 ? '+' : ''}{item.offsetDays} days
                      </span>
                    </div>
                    <div className="pv-detail-item">
                      <span className="pv-detail-label">Reference</span>
                      <span className="pv-detail-value pv-detail-ref">
                        {getOffsetLabel(item.offsetType)}
                      </span>
                    </div>
                  </div>
                  <div className="pv-list-card-minibar">
                    <div className="pv-minibar-track">
                      {isMilestone ? (
                        <div
                          className="pv-minibar-diamond"
                          style={{ left: `${posLeft}%`, borderColor: color.bar }}
                        />
                      ) : (
                        <div
                          className="pv-minibar-fill"
                          style={{ left: `${posLeft}%`, width: `${posWidth}%`, background: color.bar }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    };

    const renderEnhancedGantt = () => {
      if (!ganttData) return (
        <div className="preview-placeholder">
          <p>No timeline data available for chart view.</p>
        </div>
      );

      return (
        <div className="pv-gantt" ref={previewContainerRef}>
          <div className="pv-gantt-axis">
            <div className="pv-gantt-label-col" />
            <div className="pv-gantt-timeline-col">
              {ganttData.monthMarkers.map((m, i) => (
                <div
                  key={i}
                  className="pv-gantt-month-marker"
                  style={{ left: `${m.left}%` }}
                >
                  <span className="pv-gantt-month-label">{m.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="pv-gantt-body">
            {ganttData.bars.map((bar, idx) => {
              const isHovered = hoveredBarId === bar.id;

              return (
                <div
                  key={bar.id}
                  className={`pv-gantt-row ${isHovered ? 'pv-gantt-row--hover' : ''} ${idx % 2 === 0 ? 'pv-gantt-row--even' : ''}`}
                  onMouseEnter={() => setHoveredBarId(bar.id)}
                  onMouseLeave={() => setHoveredBarId(null)}
                >
                  <div className="pv-gantt-label-col">
                    <div className="pv-gantt-step-info">
                      <span className="pv-gantt-step-order">{idx + 1}</span>
                      <div className="pv-gantt-step-text">
                        <div className="pv-gantt-step-name">{bar.name}</div>
                        <div className="pv-gantt-step-meta">
                          {format(bar.start, 'MMM dd')}
                          {!bar.isMilestone && ` — ${format(bar.end, 'MMM dd')}`}
                          {bar.discipline && <span className="pv-gantt-step-disc" style={{ color: bar.color.fg }}> · {bar.discipline}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pv-gantt-timeline-col">
                    {ganttData.monthMarkers.map((m, i) => (
                      <div key={i} className="pv-gantt-gridline" style={{ left: `${m.left}%` }} />
                    ))}
                    {ganttData.showToday && (
                      <div className="pv-gantt-today" style={{ left: `${ganttData.todayLeft}%` }} />
                    )}
                    {bar.isMilestone ? (
                      <div
                        className="pv-gantt-milestone"
                        style={{ left: `${bar.left}%`, borderColor: bar.color.bar }}
                        title={`${bar.name} — ${format(bar.start, 'MMM dd, yyyy')}`}
                      />
                    ) : (
                      <div
                        className="pv-gantt-bar"
                        style={{
                          left: `${bar.left}%`,
                          width: `${bar.width}%`,
                          background: bar.color.bar,
                        }}
                        title={`${bar.name}\n${format(bar.start, 'MMM dd, yyyy')} → ${format(bar.end, 'MMM dd, yyyy')}\n${bar.durationDays} day(s)`}
                      >
                        {bar.width > 8 && (
                          <span className="pv-gantt-bar-label">{bar.name}</span>
                        )}
                      </div>
                    )}
                    {isHovered && (
                      <div className="pv-gantt-tooltip" style={{ left: `${Math.min(bar.left + bar.width / 2, 80)}%` }}>
                        <div className="pv-tooltip-title">{bar.name}</div>
                        <div className="pv-tooltip-row">
                          <span>Date:</span>
                          <span>{format(bar.start, 'MMM dd, yyyy')}{!bar.isMilestone && ` → ${format(bar.end, 'MMM dd, yyyy')}`}</span>
                        </div>
                        {!bar.isMilestone && (
                          <div className="pv-tooltip-row">
                            <span>Duration:</span>
                            <span>{bar.durationDays} day{bar.durationDays !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                        <div className="pv-tooltip-row">
                          <span>Offset:</span>
                          <span>{bar.offsetDays >= 0 ? '+' : ''}{bar.offsetDays}d from {getOffsetLabel(bar.offsetType)}</span>
                        </div>
                        {bar.discipline && (
                          <div className="pv-tooltip-row">
                            <span>Discipline:</span>
                            <span style={{ color: bar.color.fg, fontWeight: 600 }}>{bar.discipline}</span>
                          </div>
                        )}
                        {bar.category && (
                          <div className="pv-tooltip-row">
                            <span>Category:</span>
                            <span>{bar.category}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {ganttData.showToday && (
            <div className="pv-gantt-today-label" style={{ left: `calc(240px + ${ganttData.todayLeft}% * (100% - 240px) / 100%)` }}>
              Today
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="preview-tab">
        <div className="preview-header">
          <h2>Timeline Preview</h2>
          <div className="preview-actions">
            <button
              className="btn btn-primary"
              onClick={handleGeneratePreview}
              disabled={previewMutation.isPending}
            >
              {previewMutation.isPending ? 'Generating...' : 'Generate Preview'}
            </button>
            {previewData && (
              <>
                <button className="btn btn-secondary" onClick={handleExportPDF} title="Export as PDF">
                  Export PDF
                </button>
                <button className="btn btn-secondary" onClick={handleExportPNG} title="Export as PNG">
                  Export PNG
                </button>
              </>
            )}
          </div>
        </div>

        <div className="preview-content">
          <div className="anchor-dates-section">
            <h3>Key Project Dates</h3>
            {enabledBuiltInDates.length === 0 && customAnchors.length === 0 ? (
              <p className="form-hint" style={{ fontStyle: 'normal' }}>No key dates defined. Go to the Edit tab to add reference dates.</p>
            ) : (
              <div className="anchor-dates-grid">
                {enabledBuiltInDates.map(key => {
                  const dateInfo = BUILT_IN_DATES.find(d => d.key === key);
                  return (
                    <div key={key} className="form-group">
                      <label className="form-label">{dateInfo?.label || key}</label>
                      <input
                        type="date"
                        className="form-input"
                        value={anchorDates[key] || ''}
                        onChange={(e) => setAnchorDates(prev => ({ ...prev, [key]: e.target.value }))}
                      />
                    </div>
                  );
                })}
                {customAnchors.map(anchor => (
                  <div key={anchor.id} className="form-group">
                    <label className="form-label">{anchor.label}</label>
                    <input
                      type="date"
                      className="form-input"
                      value={anchor.date || ''}
                      onChange={(e) => handleUpdateCustomAnchorDate(anchor.id, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {previewData && (
            <>
              {renderSummaryStats()}

              {previewData.warnings && previewData.warnings.length > 0 && (
                <div className="preview-warnings">
                  <h3>Warnings</h3>
                  <ul>
                    {previewData.warnings.map((warning, index) => (
                      <li key={index} className="warning-item">{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="pv-controls-row">
                <div className="preview-view-toggle">
                  <button
                    className={`toggle-btn ${previewViewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setPreviewViewMode('list')}
                  >
                    List View
                  </button>
                  <button
                    className={`toggle-btn ${previewViewMode === 'chart' ? 'active' : ''}`}
                    onClick={() => setPreviewViewMode('chart')}
                  >
                    Chart View
                  </button>
                </div>
                {renderDisciplineLegend()}
              </div>

              <div className="preview-timeline-container">
                {previewViewMode === 'list' ? renderEnhancedListView() : renderEnhancedGantt()}
              </div>
            </>
          )}

          {!previewData && !previewMutation.isPending && (
            <div className="preview-placeholder">
              <p>Click "Generate Preview" to see the timeline visualization.</p>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Check if user can apply template (project_manager or admin)
  const canApplyTemplate = useMemo(() => {
    if (!currentEmployee) return false;
    const permissionId = parseInt(currentEmployee.permish_id?.toString() || '0');
    return permissionId === 1;
  }, [currentEmployee]);

  // Handle apply template
  const handleApplyTemplate = useCallback(() => {
    if (!templateId || templateId === 'create') {
      ToastService.warning('Please save the template first before applying');
      return;
    }
    if (!canApplyTemplate) {
      ToastService.error('You do not have permission to apply templates. Only Project Managers and Administrators can apply templates.');
      return;
    }
    setShowApplyModal(true);
  }, [templateId, canApplyTemplate]);
  
  const handleConfirmApply = useCallback(() => {
    if (!selectedProjectId || !templateId || templateId === 'create') {
      ToastService.warning('Please select a project');
      return;
    }
    
    const selectedProject = mattersData?.find((m: any) => m.matterid === selectedProjectId);
    if (!selectedProject) {
      ToastService.error('Selected project not found');
      return;
    }
    
    const payload: WorkflowTemplateApplyPayload = {
      templateId: templateId,
      projectId: selectedProjectId,
      projectName: selectedProject.mattername || selectedProjectId,
      anchorDate: applyAnchorDate,
      anchorField: 'projectKickoffDate',
      status: 'active',
    };
    
    applyTemplateMutation.mutate(payload);
  }, [selectedProjectId, templateId, applyAnchorDate, mattersData, applyTemplateMutation]);
  
  return (
    <div className="template-editor-page">
      <div className="editor-header">
        <div className="header-content">
          <button
            className="btn-back"
            onClick={() => navigate('/templates')}
            title="Back to templates"
          >
            ← Back
          </button>
          <h1>
            {isCreateMode ? 'New Template' : (templateData?.template?.name || 'Edit Template')}
          </h1>
        </div>
        {templateId && templateId !== 'create' && canApplyTemplate && (
          <div className="header-actions">
            <button
              className="btn btn-primary"
              onClick={handleApplyTemplate}
              title="Apply this template to a project"
            >
              Apply Template
            </button>
          </div>
        )}
      </div>
      
      <div className="editor-tabs">
        <button
          className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          {isCreateMode ? 'Build' : 'Edit'}
        </button>
        <button
          className={`tab-button ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => setActiveTab('preview')}
        >
          Preview
        </button>
      </div>
      
      <div className="editor-content">
        {renderActiveTab()}
      </div>
      
      {/* Apply Template Modal */}
      {showApplyModal && (
        <div className="modal-overlay" onClick={() => setShowApplyModal(false)}>
          <div className="modal-content apply-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Apply Template to Project</h2>
              <button
                className="btn-icon"
                onClick={() => setShowApplyModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label required">Project</label>
                <select
                  className="form-input"
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  required
                >
                  <option value="">Select a project...</option>
                  {mattersData?.map((matter: any) => (
                    <option key={matter.matterid} value={matter.matterid}>
                      {matter.matterid}: {matter.mattername || 'Unnamed Project'}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label required">Anchor Date (Project Start)</label>
                <input
                  type="date"
                  className="form-input"
                  value={applyAnchorDate}
                  onChange={(e) => setApplyAnchorDate(e.target.value)}
                  required
                />
                <small className="form-hint">
                  This date will be used as the reference point for calculating milestone dates
                </small>
              </div>
              
              {templateData?.steps && templateData.steps.length > 0 && (
                <div className="apply-summary">
                  <h3>Milestones to be Created</h3>
                  <p className="summary-text">
                    This template will create <strong>{templateData.steps.length}</strong> milestone(s):
                  </p>
                  <ul className="milestone-list">
                    {templateData.steps
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((step, index) => (
                        <li key={step.stepId}>
                          <strong>Step {index + 1}:</strong> {step.name}
                          {step.discipline && <span className="step-meta"> ({step.discipline})</span>}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              
              {previewData?.warnings && previewData.warnings.length > 0 && (
                <div className="apply-warnings">
                  <h3>⚠️ Warnings</h3>
                  <ul>
                    {previewData.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowApplyModal(false)}
                disabled={applyTemplateMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmApply}
                disabled={!selectedProjectId || !applyAnchorDate || applyTemplateMutation.isPending}
              >
                {applyTemplateMutation.isPending ? 'Applying...' : 'Apply Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateEditorPage;

